import {customerContext, getSalesAreaFields} from '@theme/rs-current-company-location';
import { getCustomerAppBaseUrl } from "@theme/gadget-envs";

class CreditMemos {
  constructor() {
    this.creditMemosTable = document.querySelector('.credit-memos-table__body');
    this.resultsWrapper = document.querySelector('.credit-memos-results-wrapper');
    this.searchInput = document.getElementById('credit-memos-search-input');
    this.searchButton = document.querySelector('.credit-memos-search__button');
    this.dateRangeInput = document.querySelector('#credit-memos-date-range-input');
    this.tableHeader = document.querySelector('.credit-memos-table__header');
    // Sort state
    this.currentCreditMemos = [];
    this.sortColumn = 'date'; // 'date' or 'total'
    this.sortDirection = 'desc'; // 'asc' or 'desc'
    this.pdfUrlCache = {}; // Cache PDF URLs by credit memo number

    this.init();
  }

  init() {
    if (!this.creditMemosTable) return;

    // Load credit memos on page load with default date range (30 days)
    this.getCreditMemos('30', '');

    this.initDateRange();
    this.initSearch();
    this.initSearchClear();
    this.initSorting();
  }

  initDateRange() {
    if (!this.dateRangeInput) return;

    this.dateRangeInput.addEventListener('change', () => {
      const selectedValue = this.dateRangeInput.value || '30';
      const searchTerm = this.searchInput?.value || '';
      this.getCreditMemos(selectedValue, searchTerm);
    });
  }

  initSearch() {
    if (!this.searchButton) return;

    this.searchButton.addEventListener('click', () => {
      const dateRange = this.dateRangeInput?.value || '30';
      const searchTerm = this.searchInput?.value || '';
      this.getCreditMemos(dateRange, searchTerm);
    });

    // Allow Enter key to trigger search
    if (this.searchInput) {
      this.searchInput.addEventListener('keypress', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          const dateRange = this.dateRangeInput?.value || '30';
          const searchTerm = this.searchInput?.value || '';
          this.getCreditMemos(dateRange, searchTerm);
        }
      });
    }
  }

  /** Clear (X) control inside the credit memos search field. */
  initSearchClear() {
    const searchInput = this.searchInput;
    if (!searchInput) return;

    if (searchInput.dataset.rsCreditMemoSearchInputWired !== '1') {
      searchInput.dataset.rsCreditMemoSearchInputWired = '1';
      searchInput.addEventListener('input', () => {
        this.syncSearchClearUi();
      });
    }

    const inputWrapper = searchInput.closest('.rs-input__wrapper');
    if (!inputWrapper) return;

    if (inputWrapper.dataset.rsCreditMemoSearchClearWired === '1') {
      this.searchClearBtn = inputWrapper.querySelector('.credit-memos-search-clear');
      this.syncSearchClearUi();
      return;
    }
    inputWrapper.dataset.rsCreditMemoSearchClearWired = '1';

    const clearLabel =
      typeof window.__t === 'function' ? window.__t('rs_profile_locations.clear_search') : 'Clear search';
    const clearBtn = document.createElement('button');
    clearBtn.type = 'button';
    clearBtn.className = 'rs-button-unstyled credit-memos-search-clear rs-hidden';
    clearBtn.setAttribute('aria-label', clearLabel);
    clearBtn.innerHTML =
      '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" class="rs-icon rs-icon-close" aria-hidden="true" focusable="false">' +
      '<path d="M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>' +
      '<path d="M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>' +
      '</svg>';
    clearBtn.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      searchInput.value = '';
      this.syncSearchClearUi();
      searchInput.focus();
      const dateRange = this.dateRangeInput?.value || '30';
      this.getCreditMemos(dateRange, '');
    });
    inputWrapper.appendChild(clearBtn);
    this.searchClearBtn = clearBtn;
    this.syncSearchClearUi();
  }

  syncSearchClearUi() {
    const searchInput = this.searchInput;
    const clearBtn = this.searchClearBtn;
    if (!searchInput || !clearBtn) return;
    const hasVal = (searchInput.value || '').trim().length > 0;
    clearBtn.classList.toggle('rs-hidden', !hasVal);
    searchInput.classList.toggle('credit-memos-search__input--has-value', hasVal);
  }

  initSorting() {
    const sortableHeaders = document.querySelectorAll('.credit-memos-table__sortable');

    sortableHeaders.forEach((header) => {
      header.addEventListener('click', () => {
        const column = header.textContent.includes('Date') ? 'date' : 'total';
        this.handleSort(column);
      });
    });
  }

  /**
   * Handle sort column click
   * @param {string} column - Column to sort by ('date' or 'total')
   */
  async handleSort(column) {
    // If clicking the same column, toggle direction; otherwise set to desc
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'desc';
    }

    // Sort and update the table
    const sortedCreditMemos = this.sortCreditMemos(this.currentCreditMemos, this.sortColumn, this.sortDirection);
    await this.updateCreditMemosTable(sortedCreditMemos);
    this.getPDFURLs();
    this.updateSortIndicators();
  }

  /**
   * Update sort indicators in table headers
   */
  updateSortIndicators() {
    const sortableHeaders = document.querySelectorAll('.credit-memos-table__sortable');

    sortableHeaders.forEach((header) => {
      const sortIcons = header.querySelector('.credit-memos-table__sort-icons');
      const column = header.textContent.includes('Date') ? 'date' : 'total';

      if (sortIcons) {
        if (this.sortColumn === column) {
          // Show active sort direction
          if (this.sortDirection === 'asc') {
            sortIcons.textContent = '↑';
          } else {
            sortIcons.textContent = '↓';
          }
          header.classList.add('is-sorted');
        } else {
          // Show both arrows for inactive column
          sortIcons.textContent = '↑↓';
          header.classList.remove('is-sorted');
        }
      }
    });
  }

  /**
   * Sort credit memos by specified column and direction
   * @param {any[]} creditMemos - Array of credit memo objects
   * @param {string} column - Column to sort by ('date' or 'total')
   * @param {string} direction - Sort direction ('asc' or 'desc')
   * @returns {any[]} Sorted array of credit memo objects
   */
  sortCreditMemos(creditMemos, column, direction) {
    return [...creditMemos].sort((a, b) => {
      let valueA, valueB;

      if (column === 'date') {
        // Get date from various possible field names
        valueA = a.date || a.createdDate || a.created_date || '';
        valueB = b.date || b.createdDate || b.created_date || '';

        // Convert to Date objects for comparison
        valueA = valueA ? new Date(valueA).getTime() : 0;
        valueB = valueB ? new Date(valueB).getTime() : 0;
      } else if (column === 'total') {
        // Match display field fallbacks (API returns totalNetValue)
        valueA = a.total ?? a.totalAmount ?? a.total_amount ?? a.totalNetValue ?? 0;
        valueB = b.total ?? b.totalAmount ?? b.total_amount ?? b.totalNetValue ?? 0;

        // Ensure numbers
        valueA = typeof valueA === 'number' ? valueA : parseFloat(valueA) || 0;
        valueB = typeof valueB === 'number' ? valueB : parseFloat(valueB) || 0;
      }

      // Sort based on direction
      if (direction === 'asc') {
        return valueA - valueB;
      } else {
        return valueB - valueA;
      }
    });
  }

  /**
   * Calculate date range from number of days
   * @param {string} days - Number of days (e.g., '30', '90', '180', '365', '558')
   * @returns {{dateFrom: string, dateTo: string}}
   */
  calculateDateRange(days) {
    // If the date range is custom, return the start and end dates
    if (days.includes('custom')) {
      const [startDate, endDate] = days.replace('custom:', '').split(':');
      return { dateFrom: startDate, dateTo: endDate };
    }
    else {
      // If the date range is a preset, return the start and end dates
      const dateTo = new Date().toISOString().split('T')[0];
      const dateFromDate = new Date();
      dateFromDate.setDate(dateFromDate.getDate() - parseInt(days, 10));
      const dateFrom = dateFromDate.toISOString().split('T')[0];
      return { dateFrom, dateTo };
    }
  }

  /**
   * Show loading state in the table
   */
  showLoadingState() {
    if (!this.creditMemosTable) return;

    this.updateResultsText(0, 0, 0);

    this.creditMemosTable.innerHTML = `
      <tr class="credit-memos-table__loading">
        <td class="text-center" colspan="5">
          <div class="credit-memos-table__loading-spinner">
            <svg class="credit-memos-table__spinner" xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
            </svg>
          </div>
          <p class="text-large weight-500 text-brand-black">${window.__t('loading_credit_memos')}</p>
        </td>
      </tr>
    `;
  }

  /**
   * Update the table with empty state message
   * @param {string} message1 - First message to display
   * @param {string} message2 - Second message to display
   */
  updateEmptyState(message1, message2) {
    if (!this.creditMemosTable) return;

    this.updateResultsText(0, 0, 0);

    this.creditMemosTable.innerHTML = `
      <tr class="credit-memos-table__empty">
        <td class="text-center" colspan="5">
          <svg class="credit-memos-table__icon credit-memos-table__icon-search" xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
            <path d="M28.0015 27.9996L22.2148 22.2129" stroke="#323232" stroke-width="2.66667" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M14.6667 25.3333C20.5577 25.3333 25.3333 20.5577 25.3333 14.6667C25.3333 8.77563 20.5577 4 14.6667 4C8.77563 4 4 8.77563 4 14.6667C4 20.5577 8.77563 25.3333 14.6667 25.3333Z" stroke="#323232" stroke-width="2.66667" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <p class="text-large weight-500 text-brand-black">${message1}</p>
          ${message2 ? `<p class="text-large weight-500 text-brand-black">${message2}</p>` : ''}
        </td>
      </tr>
    `;
  }

  /**
   * Update the results count text above the table
   * @param {number} start - First visible result index (1-based)
   * @param {number} end - Last visible result index (1-based)
   * @param {number} total - Total number of results
   */
  updateResultsText(start, end, total) {
    if (!this.resultsWrapper) return;

    if (!total) {
      this.resultsWrapper.textContent = '';
      return;
    }

    const showingStart = start || 1;
    this.resultsWrapper.textContent = window.__t('showing_of_results', {
      showing: `${showingStart}-${end}`,
      total,
    });
  }

  /**
   * Fetch credit memos from API
   * @param {string} dateRange - Number of days (e.g., '30', '90', '180', '365', '558')
   * @param {string} searchTerm - Search term for credit memo #, PO #, or Order #
   */
  async getCreditMemos(dateRange, searchTerm) {
    if (!dateRange) return;

    const { dateFrom, dateTo } = this.calculateDateRange(dateRange);

    // Show loading state
    this.showLoadingState();

    // Hide pagination during loading
    const paginationWrapper = document.querySelector('.credit-memos-pagination-wrapper');
    if (paginationWrapper) {
      paginationWrapper.style.display = 'none';
    }

    try {
      const data = await this.getCreditMemosAPI(dateFrom, dateTo, searchTerm);

      if (data && data.creditMemoHistoryResponse.transactions && Array.isArray(data.creditMemoHistoryResponse.transactions) && data.creditMemoHistoryResponse.transactions.length > 0) {
        // Show table header if the table is not empty
        if (this.tableHeader) this.tableHeader.classList.remove('rs-hidden');

        // Store the credit memos
        this.currentCreditMemos = data.creditMemoHistoryResponse.transactions;

        // Sort by date, newest first (default)
        this.sortColumn = 'date';
        this.sortDirection = 'desc';
        const sortedCreditMemos = this.sortCreditMemos(this.currentCreditMemos, this.sortColumn, this.sortDirection);
        await this.updateCreditMemosTable(sortedCreditMemos);
        this.getPDFURLs();
        this.updateSortIndicators();
      } else {
        this.currentCreditMemos = [];
        // Hide pagination when no results
        const paginationWrapper = document.querySelector('.credit-memos-pagination-wrapper');
        if (paginationWrapper) {
          paginationWrapper.style.display = 'none';
        }

        if (searchTerm) {
          this.updateEmptyState(window.__t('no_credit_memos_found'), window.__t('use_search_or_date_range'));
        } else {
          this.updateEmptyState(window.__t('no_results_to_display'), window.__t('use_search_or_date_range'));
        }

        // Hide table header if the table is empty
        if (this.tableHeader) this.tableHeader.classList.add('rs-hidden');
      }
    } catch (error) {
      console.error('Error fetching credit memos:', error);
      this.currentCreditMemos = [];
      if (this.tableHeader) this.tableHeader.classList.add('rs-hidden');
      const paginationEl = document.querySelector('.credit-memos-pagination-wrapper');
      if (paginationEl) paginationEl.style.display = 'none';
      this.updateEmptyState(window.__t('no_results_to_display'), window.__t('use_search_or_date_range'));
    }
  }

  getPDFURLs() {
    const pdfButtons = document.querySelectorAll('.credit-memos-table__pdf-link');
    pdfButtons.forEach((button) => {
      button.addEventListener('click', async (e) => {
        e.preventDefault();
        const creditMemoNumber = button.dataset.creditMemoNumber;
        const pdfUrl = await this.getPDFURL(creditMemoNumber);
        if (pdfUrl) {
          button.href = pdfUrl;
          window.open(pdfUrl, '_blank');
        } else {
          // Show error toat message
          this.showToast();
        }
      });
    });
  }

  showToast() {
    document.querySelectorAll('.credit-memos-toast').forEach((el) => el.remove());

    document.body.insertAdjacentHTML('beforeend', `<div class="credit-memos-toast" role="alert" aria-live="assertive" aria-atomic="true">
      <div class="credit-memos-toast__content">
        <svg class="credit-memos-toast__icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
          <path d="M18.109 14.9999L11.4423 3.33319C11.297 3.0767 11.0862 2.86335 10.8314 2.71492C10.5767 2.56649 10.2872 2.48828 9.99234 2.48828C9.69752 2.48828 9.40797 2.56649 9.15324 2.71492C8.8985 2.86335 8.6877 3.0767 8.54234 3.33319L1.87567 14.9999C1.72874 15.2543 1.6517 15.5431 1.65235 15.837C1.653 16.1308 1.73132 16.4192 1.87938 16.673C2.02744 16.9269 2.23996 17.137 2.49542 17.2822C2.75088 17.4274 3.04018 17.5025 3.33401 17.4999H16.6673C16.9598 17.4996 17.2469 17.4223 17.5001 17.2759C17.7532 17.1295 17.9634 16.9191 18.1094 16.6658C18.2555 16.4125 18.3324 16.1252 18.3323 15.8328C18.3322 15.5404 18.2552 15.2531 18.109 14.9999Z" stroke="currentColor" stroke-width="1.45833" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M10 7.5V10.8333" stroke="currentColor" stroke-width="1.45833" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M10 14.1667H10.0083" stroke="currentColor" stroke-width="1.45833" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <p class="credit-memos-toast__message">PDF unavailable. Please try again later.</p>
        <button class="credit-memos-toast__close rs-button-unstyled" type="button" aria-label="Close notification">
          <svg class="rs-icon rs-icon-close" aria-hidden="true" focusable="false" xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M10.7197 0.219727C11.0126 -0.0731381 11.4874 -0.0731574 11.7803 0.219727C12.0729 0.512631 12.0731 0.987463 11.7803 1.28027L7.06055 6L11.7803 10.7197L11.8311 10.7764C12.0713 11.0709 12.0548 11.5057 11.7803 11.7803C11.5057 12.0548 11.0709 12.0722 10.7764 11.832L10.7197 11.7803L6 7.06055L1.28027 11.7803C0.987466 12.073 0.512632 12.0729 0.219727 11.7803C-0.0731579 11.4874 -0.0731387 11.0126 0.219727 10.7197L4.93945 6L0.219727 1.28027C-0.0731675 0.98738 -0.0731675 0.51262 0.219727 0.219727C0.512628 -0.0730756 0.98741 -0.0731365 1.28027 0.219727L6 4.93945L10.7197 0.219727Z" fill="#323232"/>
          </svg>
        </button>
      </div>
    </div>`);

    const toast = document.querySelector('.credit-memos-toast');
    const closeButton = toast?.querySelector('.credit-memos-toast__close');
    closeButton?.addEventListener('click', () => toast?.remove());
    setTimeout(() => toast?.remove(), 3000);
  }

  /**
   * Make API request to fetch credit memos
   * @param {string} dateFrom - Start date (YYYY-MM-DD)
   * @param {string} dateTo - End date (YYYY-MM-DD)
   * @param {string} searchTerm - Search term
   * @returns {Promise<any>} API response
   */
  async getCreditMemosAPI(dateFrom, dateTo, searchTerm) {
    const salesAreaFields = getSalesAreaFields(customerContext);
    const salesOrg = salesAreaFields.sales_org_id || '';
    const distributionChannel = salesAreaFields.distribution_channel_id || '';
    const division = salesAreaFields.division_id || '';
    //soldTo = company's externalId
    const soldTo = customerContext?.company_external_id;

    // Get shipTo from current location or use soldTo as fallback
    //shipTo = location's shipToId
    const shipTo = customerContext?.ship_to_id;

    const response = await fetch(`${getCustomerAppBaseUrl()}/api/credit-memos`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${window.myJWT}`,
      },
      body: JSON.stringify({
        salesOrg: salesOrg,
        distributionChannel: distributionChannel,
        division: division,
        soldTo: soldTo,
        shipTo: [shipTo],
        filters: {
          searchTerm: searchTerm || '',
          dateFrom: dateFrom,
          dateTo: dateTo,
        },
      }),
    });

    if (!response.ok) {
      throw new Error(`API request failed: ${response.status}`);
    }

    const data = await response.json();
    return data;
  }


  /**
   * Update the table with credit memo data
   * @param {any[]} creditMemos - Array of credit memo objects
   */
  async updateCreditMemosTable(creditMemos) {
    if (!this.creditMemosTable) return;

    // Get translation labels from table data attributes
    const table = this.creditMemosTable.closest('.credit-memos-table');
    const labelCreditMemo = table?.dataset.labelCreditMemo || 'Credit Memo #';
    const labelDate = table?.dataset.labelDate || 'Date';
    const labelPo = table?.dataset.labelPo || 'PO #';
    const labelTotal = table?.dataset.labelTotal || 'Total (USD)';
    const labelPdf = table?.dataset.labelPdf || 'PDF';
    const labelDownloadPdf = table?.dataset.labelDownloadPdf || 'Download PDF';

    // Render all table rows and wait for PDF URLs
    const rowPromises = creditMemos.map(async (creditMemo, index) => {
      const creditMemoNumber = creditMemo.creditMemoNumber || creditMemo.credit_memo_number || creditMemo.number || '';
      const date = creditMemo.date || creditMemo.createdDate || creditMemo.created_date || '';
      const poNumber = creditMemo.poNumber || creditMemo.po_number || '';
      const total = creditMemo.total || creditMemo.totalAmount || creditMemo.total_amount || creditMemo.totalNetValue || '';

      // Format date
      let formattedDate = '';
      if (date) {
        try {
          formattedDate = new Date(date).toLocaleDateString();
        } catch (e) {
          formattedDate = date;
        }
      }

      // Format total as currency
      const formattedTotal = typeof total === 'number'
        ? total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : total;

      return `
        <tr class="credit-memos-table__row" data-index="${index + 1}">
          <td class="text-medium text-brand-black">
            <p class="text-medium weight-500 text-brand-black rs-desktop-hidden">${labelCreditMemo}:</p>
            ${creditMemoNumber || ''}
          </td>
          <td class="text-medium text-brand-black">
            <p class="text-medium weight-500 text-brand-black rs-desktop-hidden">${labelDate}:</p>
            ${formattedDate || ''}
          </td>
          <td class="text-medium text-brand-black">
            <p class="text-medium weight-500 text-brand-black rs-desktop-hidden">${labelPo}:</p>
            ${poNumber || ''}
          </td>
          <td class="text-medium text-brand-black">
            <p class="text-medium weight-500 text-brand-black rs-desktop-hidden">${labelTotal}:</p>
            $${formattedTotal || '0.00'}
          </td>
          <td class="text-medium">
            <a href='' class="credit-memos-table__pdf-link" target="_blank" rel="noopener noreferrer" data-credit-memo-number="${creditMemoNumber}">
              <span class="credit-memos-table__pdf-text rs-desktop-hidden">${labelDownloadPdf}</span>
              <span class="credit-memos-table__pdf-text rs-mob-hidden">${labelPdf}</span>
              <svg class="credit-memos-table__pdf-icon" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                <polyline points="7 10 12 15 17 10"></polyline>
                <line x1="12" y1="15" x2="12" y2="3"></line>
              </svg>
            </a>
          </td>
        </tr>
      `;
    });

    // Wait for all rows to be prepared (including PDF URLs)
    const rows = await Promise.all(rowPromises);

    // Now clear loading state and render all rows at once
    this.creditMemosTable.innerHTML = rows.join('');

    // Initialize pagination if there are more than 10 results
    if (creditMemos.length > 10) {
      // Use setTimeout to ensure DOM is updated
      setTimeout(() => {
        this.initPagination(creditMemos.length);
      }, 0);
    } else {
      // Hide pagination if 10 or fewer results
      const paginationWrapper = document.querySelector('.credit-memos-pagination-wrapper');
      if (paginationWrapper) {
        paginationWrapper.style.display = 'none';
      }
      this.updateResultsText(1, creditMemos.length, creditMemos.length);
    }
  }

  /**
   * Get PDF URL for a credit memo
   * @param {string} creditMemoNumber - Credit memo number
   * @returns {Promise<string|null>} PDF URL or null if error
   */
  async getPDFURL(creditMemoNumber) {
    try {
      const response = await fetch(`${getCustomerAppBaseUrl()}/api/transactions/document?documentNumber=${creditMemoNumber}&documentType=INVOICE&soldTo=${customerContext?.company_external_id}`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${window.myJWT}`,
        },
      });

      // Check if response is PDF
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/pdf')) {
        // Create blob URL from PDF response
        const blob = await response.blob();
        return URL.createObjectURL(blob);
      }

      // Try to parse as JSON if it's not a PDF
      try {
        const data = await response.json();
        // If API returns a URL or base64 data
        if (data.url) return data.url;
        if (data.attachmentHEX) return data.attachmentHEX;
        if (data.pdfUrl) return data.pdfUrl;
      } catch (jsonError) {
        // If JSON parsing fails, it might be a PDF anyway
        const blob = await response.blob();
        return URL.createObjectURL(blob);
      }

      return null;
    } catch (error) {
      console.error('Error fetching PDF:', error);
      return null;
    }
  }

  /**
   * Initialize pagination for the credit memos table
   * @param {number} totalItems - Total number of credit memos
   */
  initPagination(totalItems) {
    const paginationWrapper = document.querySelector('.credit-memos-pagination-wrapper');
    if (!paginationWrapper) return;

    // Show pagination if there are items
    if (totalItems > 0) {
      paginationWrapper.style.display = 'flex';
    } else {
      paginationWrapper.style.display = 'none';
      return;
    }

    const rows = document.querySelectorAll('.credit-memos-table__row');
    const optionButtons = document.querySelectorAll('.credit-memos-pagination__rows-options .rs-cart-pagination__rows-option');
    const selectedOptionBtn = document.querySelector('.credit-memos-pagination__rows-options .rs-cart-pagination__rows-option.rs-active');
    const rowSelectBtn = document.querySelector('.credit-memos-pagination__rows-button');
    const prevBtn = document.getElementById('credit-memos-pagination-prev');
    const nextBtn = document.getElementById('credit-memos-pagination-next');
    const pageInfo = document.querySelector('.credit-memos-pagination__info');
    const rowsOptions = document.querySelector('.credit-memos-pagination__rows-options');

    if (!rows.length || !optionButtons.length || !prevBtn || !nextBtn || !pageInfo) return;

    // Remove existing event listeners by cloning and replacing elements
    const newPrevBtn = prevBtn.cloneNode(true);
    const newNextBtn = nextBtn.cloneNode(true);
    prevBtn.parentNode.replaceChild(newPrevBtn, prevBtn);
    nextBtn.parentNode.replaceChild(newNextBtn, nextBtn);

    // Clone row select button early so we can reference it in option handlers
    let newRowSelectBtn = null;
    if (rowSelectBtn) {
      newRowSelectBtn = rowSelectBtn.cloneNode(true);
      rowSelectBtn.parentNode.replaceChild(newRowSelectBtn, rowSelectBtn);
    }

    // Clone option buttons to remove listeners
    const newOptionButtons = [];
    optionButtons.forEach((btn) => {
      const newBtn = btn.cloneNode(true);
      btn.parentNode.replaceChild(newBtn, btn);
      newOptionButtons.push(newBtn);
    });

    let currentPage = 1;
    let rowsPerPage = parseInt(selectedOptionBtn?.value || '10', 10);
    let totalPages = Math.max(1, Math.ceil(totalItems / rowsPerPage));

    const renderPage = () => {
      totalPages = Math.max(1, Math.ceil(totalItems / rowsPerPage));

      if (currentPage > totalPages) currentPage = totalPages;
      if (currentPage < 1) currentPage = 1;

      const startIndex = (currentPage - 1) * rowsPerPage + 1;
      const endIndex = Math.min(startIndex + rowsPerPage - 1, totalItems);

      rows.forEach((row) => {
        const idx = parseInt(row.dataset.index, 10);
        if (idx >= startIndex && idx <= endIndex) {
          row.classList.remove('rs-hidden');
        } else {
          row.classList.add('rs-hidden');
        }
      });

      pageInfo.textContent = `${startIndex}-${endIndex} of ${totalItems}`;
      this.updateResultsText(startIndex, endIndex, totalItems);
      newPrevBtn.disabled = currentPage === 1;
      newNextBtn.disabled = currentPage === totalPages;
    };

    // Add event listeners for pagination buttons
    newNextBtn.addEventListener('click', () => {
      if (currentPage < totalPages) {
        currentPage++;
        renderPage();
      }
    });

    newPrevBtn.addEventListener('click', () => {
      if (currentPage > 1) {
        currentPage--;
        renderPage();
      }
    });

    newOptionButtons.forEach((option) => {
      option.addEventListener('click', () => {
        newOptionButtons.forEach((btn) => btn.classList.remove('rs-active'));
        option.classList.add('rs-active');
        rowsPerPage = parseInt(option.value, 10);
        currentPage = 1;
        if (newRowSelectBtn) {
          const span = newRowSelectBtn.querySelector('span');
          if (span) {
            span.textContent = window.__t('show_count_rows', { count: option.value });
          }
        }
        renderPage();
      });
    });

    // Handle dropdown toggle
    if (newRowSelectBtn) {
      newRowSelectBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        const isExpanded = newRowSelectBtn.getAttribute('aria-expanded') === 'true';
        newRowSelectBtn.setAttribute('aria-expanded', String(!isExpanded));
        if (rowsOptions) {
          rowsOptions.style.display = isExpanded ? 'none' : 'block';
        }
      });

      // Close dropdown when clicking outside
      const clickHandler = (e) => {
        if (newRowSelectBtn && rowsOptions && !newRowSelectBtn.contains(e.target) && !rowsOptions.contains(e.target)) {
          newRowSelectBtn.setAttribute('aria-expanded', 'false');
          rowsOptions.style.display = 'none';
        }
      };
      document.addEventListener('click', clickHandler);
    }

    renderPage();
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const checkJWT = () => {
    if (window.myJWT) {
      new CreditMemos();
    } else {
      setTimeout(checkJWT, 100);
    }
  };
  checkJWT();
});
